/**
 * Domain types mirroring the FastAPI contract.
 * Keep in sync with `teams_app/my_teams_tab/src/my_teams_tab/schemas/`.
 */

export type AggregateStatus =
  | "pending_review"
  | "approved"
  | "archive_only"
  | "archived"
  | "failed"
  | "mixed";

export type ApprovalStatus =
  | "pending_review"
  | "approved"
  | "archived"
  | "removed";

export type SortOrder = "newest" | "oldest";

export interface StatusCounts {
  pendingReview: number;
  approved: number;
  archived: number;
  removed: number;
  failed: number;
}

export interface PipelineErrorItem {
  stage: string;
  message: string | null;
  createdAt: string | null;
}

export interface Choice {
  value: unknown;
  label: string;
}

export type FieldKind = "value" | "choice";
export type FieldType = "datetime" | "number" | "string" | "boolean";

export interface FieldSpec {
  name: string;
  label: string;
  kind: FieldKind;
  type: FieldType;
  required: boolean;
  default?: unknown;
  choices?: Choice[] | null;
  defaultChoiceIndex?: number | null;
  /** Informational note rendered under the field label. Today used by
   * BL/QC to surface the parcel's primary/secondary UOM ("Parcel expects
   * BBL") so the operator picks the matching variant. */
  hint?: string | null;
}

export interface SymphonyFieldSet {
  documentType: string;
  editableFields: FieldSpec[];
  /** When true, the form fetches the parcel's actuals and renders an
   * "Actual to update" picker — the chosen ``actualNumber`` is included
   * in the push values payload. */
  requiresActualPicker: boolean;
}

export interface SymphonyPushError {
  detail: string;
  statusCode?: number | null;
  body?: unknown;
}

export interface ParcelSummary {
  parcelNumber: number;
  parcelStatus?: number | null;
  tradeNumber?: number | null;
  orderNumber?: number | null;
  itemNumber?: number | null;
  nominatedQuantity?: number | null;
  deliveryFromDate?: string | null;
  deliveryEndDate?: string | null;
}

/** Subset of Symphony's ParcelViewDto that the preview modal renders.
 * The API returns the full DTO via `exclude_none=True`; the UI only types
 * the fields it cares about and treats the rest as opaque.
 */
export interface ParcelViewDto {
  parcelNumber?: number | null;
  parcelStatus?: number | null;
  parcelStatusDescription?: string | null;
  tradeNumber?: number | null;
  orderNumber?: number | null;
  itemNumber?: number | null;
  nominatedQuantity?: number | null;
  deliveryFromDate?: string | null;
  deliveryEndDate?: string | null;
  billOfLadingInfo?: Record<string, unknown> | null;
  loadingDischargeInfo?: Record<string, unknown> | null;
  actuals?: Array<Record<string, unknown>> | null;
  allocationItem?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface DocumentListItem {
  id: string;
  title: string;
  documentType: string;
  status: string;
  approvalStatus: string;
  errorMessage: string | null;
  emailSubject: string;
  emailFrom: string;
  emailDate: string | null;
  emailLink: string | null;
  sourceType: string;
  attachmentName: string | null;
  sourcePages: string | null;
  documentFolder: string | null;
  processedAt: string | null;
  documentFolderPath: string;
  /** Browser-openable SharePoint URL; populated only on detail responses. */
  documentFolderUrl: string | null;
  emailMessageIdHash: string;
  /** Effective trade — override if set, otherwise the email's trade. */
  tradeKey: string | null;
  /** Actual per-doc override (null = inheriting). */
  tradeKeyOverride: string | null;
  /** The parent email's trade key, for inheritance UI context. */
  emailTradeKey: string | null;
}

export interface EmailSummary {
  id: string;
  subject: string;
  from: string;
  to: string;
  receivedAt: string | null;
  emailLink: string | null;
  folderLink: string | null;
  documentCount: number;
  aggregateStatus: AggregateStatus;
  statusCounts: StatusCounts;
  tradeKey: string | null;
  documentTypes: string[];
}

export interface EmailDetail extends EmailSummary {
  documents: DocumentListItem[];
  selectedParcelNumber: number | null;
  parcelCandidates: ParcelSummary[];
  parcelFetchStatus: string | null;
}

export interface MotCandidate {
  motCode: string;
  motFullName: string;
  score: number;
  imoNum: string | null;
}

export interface CreateVesselResponse {
  success: boolean;
  message: string;
}

/** Fuzzy trade match returned by ``GET /api/trades/match-by-email``.
 *
 * Trade direction (``S`` / ``B``) is NOT carried — Fabric doesn't expose
 * it. The operator picks the prefix when committing the ``trade_key``. */
export interface TradeMatchCandidate {
  tradeNumber: number;
  orderNumber: number;
  itemNumber: number;
  score: number;
  scoreBreakdown: Record<string, number>;
  parcelNumbers: number[];
  motName: string | null;
  imoNum: string | null;
  scheduleFrom: string | null;
  scheduleTo: string | null;
  cptyShortName: string | null;
  cptyLegalName: string | null;
  product: string | null;
  productGroup: string | null;
  loadPort: string | null;
  dischargePort: string | null;
}

/** Echo of the inputs the backend used to score against Fabric. Surfaced
 * in the response so the FE can render a field-by-field comparison. */
export interface TradeMatchQueryEcho {
  vesselName: string | null;
  counterparties: string[];
  productGrade: string | null;
  ports: string[];
  laycanStart: string | null;
  laycanEnd: string | null;
  cpDate: string | null;
}

export interface TradeMatchResponse {
  query: TradeMatchQueryEcho;
  candidates: TradeMatchCandidate[];
}

export interface DocumentDetail {
  item: DocumentListItem;
  pages: Array<Record<string, unknown>>;
  textContent: string;
  extractedFields: Record<string, unknown>;
  errorLog: string | null;
  pipelineErrors: PipelineErrorItem[];
  selectedParcelNumber: number | null;
  parcelCandidates: ParcelSummary[];
  parcelFetchStatus: string | null;
  symphonyFieldSet: SymphonyFieldSet;
  motCandidates: MotCandidate[];
  symphonyPayloadData?: Record<string, unknown> | null;
}

export interface ListEmailsResponse {
  emails: EmailSummary[];
  nextToken: string | null;
}

export interface DocumentType {
  key: string;
  label: string;
  archiveOnly: boolean;
}

export function isArchiveOnly(
  docType: string,
  types: DocumentType[] | undefined,
): boolean {
  if (!types) return false;
  return types.find((t) => t.key === docType)?.archiveOnly ?? false;
}

export interface DocumentTypesResponse {
  documentTypes: DocumentType[];
}

/**
 * Which list mode is active on the main page. ``emails`` = grouped by
 * email (default, the legacy behavior); ``documents`` = flat per-doc.
 */
export type ListMode = "emails" | "documents";

/**
 * Top-level tab axis, shared between Emails and Documents modes.
 * "Mixed" was dropped — every decided record is approved/archived/failed,
 * and the rest are needs-review.
 */
export type ListTab =
  | "all"
  | "needsCategorization"
  | "needsReview"
  | "approved"
  | "archived"
  | "failed";

export interface FilterState {
  subject: string;
  from: string;
  documentType: string | null;
  /** Selected tab — applies in both modes with mode-specific semantics. */
  listTab: ListTab;
  dateFrom: string | null;
  dateTo: string | null;
  sort: SortOrder;
  /** Shared: also applies in both modes. */
  tradeKey: string;
  /** Documents-mode only: restrict to docs with corrected_data. */
  correctedOnly: boolean;
  mode: ListMode;
}

export const emptyFilters: FilterState = {
  subject: "",
  from: "",
  documentType: null,
  listTab: "needsReview",
  dateFrom: null,
  dateTo: null,
  sort: "newest",
  tradeKey: "",
  correctedOnly: false,
  mode: "emails",
};

export interface ListTabCounts {
  all: number;
  needsCategorization: number;
  needsReview: number;
  approved: number;
  archived: number;
  failed: number;
}

export interface ListDocumentsResponse {
  documents: DocumentListItem[];
  nextToken: string | null;
}
